import { useContext, useEffect, useState } from 'react';
import { useRouter } from 'next/router';

import { Button, Pagination, Typography } from '@mui/material';
import DescriptionIcon from '@mui/icons-material/Description';

import { RequestNextApi } from '../../../../api/Request';
import { ProgressContext } from '../../../../components/Context/progressContext';
import Header from '../../../../components/Header';
import { headerLink } from '../../../../navigation/function';
import { accountNavigation } from '../../../../navigation/account';
import { getCookie } from '../../../../utils/cookieHandle';
import { ShowNotification } from '../../../../utils/notification/snackbarUtils';
import { StyledDataGrid } from '../../../../components/DataGrid/StyledDataGrid';
import TableOverlay from '../../../../components/DataGrid/TableOverlay';
import {CloudDownload} from '@mui/icons-material';

export default function TransactionHistory() {
    const { state: progressContext, update: updateProgress } = useContext(ProgressContext);

    const [transactions, setTransactions] = useState([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [pages, setPages] = useState(null);

    const accountLink = headerLink(accountNavigation.account);
    const pageLink = headerLink(accountNavigation.history, true);

    const router = useRouter();

    useEffect(() => {
        fetchTransactionList(1, 10);
    }, []);

    const fetchTransactionList = async (page, limit) => {
        try {
            const result = await RequestNextApi.post('/transaction-history/transaction-fetch-list', {
                page,
                limit,
                jwtToken: getCookie('jwt-token'),
            });
            if (result.data.success) {
                setTransactions(result.data.transactions);
                setPages(Math.ceil(parseInt(result.data.pagination.rows) / 10));
            } else {
                ShowNotification.error(result.data.message);
            }
        } catch (error) {
            ShowNotification.error('Failed Change Email');
        }
    };

    const handlePageChange = async (event, value) => {
        setTableLoading(true);
        await fetchTransactionList(value, 10);
        setTableLoading(false);
    };

    const handleClickInvoice = (id) => {
        router.push(`/members/account/transaction-history/${id}`);
    };

    const columns = [
        {
            field: 'id',
            headerName: 'Invoice',
            headerClassName: 'bg-gray-50',
            className: 'bg-white',
            flex: 1,
            minWidth: 100,
            sortable: false,
            renderCell: (params) => (
                <div className='h-full' onClick={() => handleClickInvoice(params.value)}>
                    <div className='flex items-center space-x-1 font-bold text-primary-500 mt-[8px] mb-[7px] cursor-pointer'>
                        <DescriptionIcon className='text-secondary-500' fontSize='12px' />
                        <div style={{ textDecoration: 'underline' }}>{params.value}</div>
                    </div>

                </div>
            ),
        },
        {
            field: 'date',
            headerName: 'Posted',
            headerClassName: 'bg-gray-50',
            flex: 1,
            minWidth: 100,
            sortable: false,
            renderCell: (params) => (
                <div className='h-full'>
                    <div className='mt-[8px]'>{params.value}</div>
                </div>
            ),
        },
        {
            field: 'qty',
            headerName: 'Qty',
            headerClassName: 'bg-gray-50',
            headerAlign: 'right',
            align: 'right',
            minWidth: 100,
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <div className='h-full grid grid-flow-row auto-rows-fr'>
                    {params.row.lines.map((item, index) => (
                        <div className='flex h-full mt-[8px]' key={index}>
                            {item.qty}
                        </div>
                    ))}
                </div>
            ),
        },
        {
            field: 'item',
            headerName: 'Item',
            headerClassName: 'bg-gray-50',
            headerAlign: 'right',
            align: 'right',
            minWidth: 60,
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <div className='h-full grid grid-flow-row auto-rows-fr'>
                    {params.row.lines.map((item, index) => (
                        <div className='flex h-full mt-[8px]' key={index}>
                            {item.item}
                        </div>
                    ))}
                </div>
            ),
        },
        {
            field: 'description',
            headerName: 'Description',
            headerClassName: 'bg-gray-50',
            minWidth: 250,
            flex: 3,
            sortable: false,
            renderCell: (params) => (
                <div className='h-full grid grid-flow-row auto-rows-fr'>
                    {params.row.lines.map((item, index, lines) => {
                        if (item.item == "4001") {
                            return <div className={`flex h-full mt-[8px] ${index == lines.length - 1 ? "mb-[7px]" : ""}`} key={index}>
                                Payment for Invoice {item.description ? "#" + item.description : ""}
                            </div>
                        }
                        if (item.item == "4000") {
                            return <div className={`flex h-full mt-[8px] ${index == lines.length - 1 ? "mb-[7px]" : ""}`} key={index}>
                                Invoice {item.description ? "#" + item.description : ""}
                            </div>
                        }
                        return (
                            <div className={`flex h-full mt-[8px] ${index == lines.length - 1 ? "mb-[7px]" : ""}`} key={index}>
                                {item.description}
                            </div>
                        )
                    })}
                </div>
            ),
        },
        {
            field: 'subtotal',
            headerName: 'Subtotal',
            headerClassName: 'bg-gray-50',
            minWidth: 60,
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <div className='h-full grid grid-flow-row auto-rows-fr'>
                    {params.row.lines.map((item, index) => (
                        <div className='flex h-full mt-[8px]' key={index}>
                            {item.price}
                        </div>
                    ))}
                </div>
            ),
        },
        {
            field: 'total',
            headerName: 'Total',
            headerClassName: 'bg-gray-50',
            minWidth: 60,
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <div className='h-full'>
                    <div className='mt-[8px]'>
                        {params.row?.invoice?.id ? params.row.invoice.total_amount : params.row.total}
                    </div>
                </div>
            ),
        },
        {
            field: 'balance',
            headerName: 'Balance',
            headerClassName: 'bg-gray-50',
            minWidth: 60,
            flex: 1,
            sortable: false,
            renderCell: (params) => (
                <div className='h-full'>
                    <div className='mt-[8px]'>
                        {Number(params.row?.invoice?.amount_due || 0).toFixed(2)}
                    </div>
                </div>
            ),
        },
    ];
    const handleClickDownload = async (userId) => {
        try {
            const result = await RequestNextApi.post(
              '/donation-statement',
              {
                  jwtToken: getCookie('jwt-token'),
              },
              {
                  responseType: 'arraybuffer',
              }
            );
            if (result.data) {
                const pdf = new Blob([result.data], { type: 'application/pdf' });
                const pdfUrl = URL.createObjectURL(pdf);

                window.open(pdfUrl);
            } else {
                ShowNotification.error('Fetch statement failed');
            }
        } catch (error) {
            console.log(error);
            ShowNotification.error('Fetch statement failed');
        }
    };

    return (
        <div>
            <Header>
                <div>
                    Members | {accountLink} | {pageLink}
                </div>
            </Header>
            <div className='flex justify-between items-center'>
                <Typography variant='h3' color='black.main' className='mt-4'>
                    Transaction History
                </Typography>
                <div className='flex gap-4'>
                    <Button variant='contained' className='mt-4 bg-secondary-500 hover:bg-secondary-500 text-white' onClick={() => router.push('/members/account/transaction-history/payments/pending')}>
                        Pending payments
                    </Button>
                    <Button variant='contained' className='mt-4 bg-secondary-500 hover:bg-secondary-500 text-white' startIcon={<CloudDownload />} onClick={() => handleClickDownload()}>
                        Download Giving Statement
                    </Button>
                </div>
            </div>
            <div className='mt-4 bg-white border px-5 py-6 rounded-xl border-solid border-primary-100 shadow'>
                {transactions?.length > 0 ? (
                    <div>
                        <StyledDataGrid rows={transactions} columns={columns} hideFooter={true} autoHeight loading={tableLoading} components={{ LoadingOverlay: TableOverlay }} getRowHeight={() => 'auto'} disableColumnFilter={true} sx={{
                            '& .MuiDataGrid-cell': {
                                alignItems: 'flex-start',
                                py: 2,
                                cursor: "default"
                            },
                        }} />
                        <div className='mt-5 flex justify-end'>
                            <Pagination count={pages} onChange={handlePageChange} />
                        </div>
                    </div>
                ) : (
                    <Typography variant='body1'>No transactions found</Typography>
                )}
            </div>
        </div>
    );
}
